import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Mail, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import api from '../api/client';

export default function PlatformEmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({
    sendgrid_domain: '',
    sendgrid_from_email: '',
    sendgrid_from_name: '',
  });
  const [status, setStatus] = useState(null); // { verified: bool, checked_at }
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/super-admin/email-config');
      setForm({
        sendgrid_domain: data.config.sendgrid_domain || '',
        sendgrid_from_email: data.config.sendgrid_from_email || '',
        sendgrid_from_name: data.config.sendgrid_from_name || '',
      });
      setStatus(data.status || null);
      setUpdatedAt(data.config.updated_at || null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load platform email config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/super-admin/email-config', form);
      toast.success('Platform email config saved');
      setStatus(data.status || null);
      setUpdatedAt(data.config.updated_at || null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      const { data } = await api.get('/super-admin/email-config?verify=1');
      setStatus(data.status || null);
      toast.success(data.status?.verified ? 'Domain verified' : 'Domain not verified');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification check failed');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-rose-50 flex items-center justify-center">
          <Mail className="h-5 w-5 text-rose-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Email</h1>
          <p className="text-sm text-gray-500">SendGrid-managed domain used for Platform Default and Verified Domain tiers</p>
        </div>
      </div>

      {/* Current status */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Current SendGrid Domain</p>
            <p className="text-sm text-gray-600 mt-1">
              {form.sendgrid_domain || <span className="text-gray-400 italic">Not configured</span>}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              From: <strong>{form.sendgrid_from_email || '—'}</strong>
              {form.sendgrid_from_name ? ` (${form.sendgrid_from_name})` : ''}
            </p>
            {updatedAt && (
              <p className="text-xs text-gray-400 mt-1">Last updated: {new Date(updatedAt).toLocaleString()}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {status?.verified ? (
              <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Verified
              </span>
            ) : (
              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                <XCircle className="h-3 w-3" /> Not Verified
              </span>
            )}
            <button
              onClick={handleCheckVerification}
              disabled={checking || !form.sendgrid_domain}
              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full font-medium flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${checking ? 'animate-spin' : ''}`} /> Check
            </button>
          </div>
        </div>
      </div>

      {/* Edit form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900">Update Platform Email Configuration</h2>

        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Domain</label>
          <input
            type="text"
            placeholder="mail.sixty10.com"
            value={form.sendgrid_domain}
            onChange={e => setForm(p => ({ ...p, sendgrid_domain: e.target.value }))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-500"
            required
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">From Email</label>
          <input
            type="email"
            placeholder="notifications@sixty10.com"
            value={form.sendgrid_from_email}
            onChange={e => setForm(p => ({ ...p, sendgrid_from_email: e.target.value }))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-500"
            required
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">From Name</label>
          <input
            type="text"
            placeholder="Sixty10"
            value={form.sendgrid_from_name}
            onChange={e => setForm(p => ({ ...p, sendgrid_from_name: e.target.value }))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="text-sm bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
